import PopupBasic from 'src/game/components/popups/PopupBasic';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { Card, CardState } from '../components/Card';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import { CardId, cardsUnlockLevel } from 'src/replicant/cards/cards.ruleset';
import i18n from 'src/lib/i18n/i18n';
import type { CardsDeliveryMethod } from 'src/replicant/cards/cards.analytics';

import { AssetGroup } from '@play-co/timestep-core';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { downscale } from 'src/conf/scale';
import CardsDailyLimitTimer from '../components/CardsDailyLimitTimer';
import {
  cardsSendRequestResponse,
  type CardsSendRequestResponseArgs,
} from '../cards.sequence';
import { assertNever } from 'src/replicant/utils';

type InviteRequestState = {
  requestedCardId: CardId;
};

/**
 * Controls whether the card response popup offers an OK action or the legacy
 * send/cancel action pair.
 */
type PopupCardResponseActionMode = 'ok' | 'send';

/**
 * Options used to configure the Cards response popup for request responses and
 * share-link status messages.
 */
type PopupCardResponseCardOpenArgs = {
  cardState: CardState;
  targetId?: string;
  inviteRequest?: InviteRequestState;
  requestMethod?: CardsDeliveryMethod;
  title?: string;
  message: string;
  actionMode: PopupCardResponseActionMode;
  buttonText?: string;
};

/**
 * Options used to show a received-card limit response with a reset countdown.
 */
type PopupCardResponseReceiveLimitOpenArgs = {
  variant: 'receiveLimit';
};

/**
 * Options used to configure a Cards response popup.
 */
export type PopupCardResponseOpenArgs =
  | PopupCardResponseCardOpenArgs
  | PopupCardResponseReceiveLimitOpenArgs;

const POPUP_WIDTH = 614;
const POPUP_HEIGHT = 845;
const CONTENT_CENTER_X = POPUP_WIDTH / 2;
const TITLE_BANNER_Y = 20;
const CARD_SCALE = 1.8;
const CARD_Y = 330;
const MESSAGE_Y = 625;
const BUTTON_Y = POPUP_HEIGHT - 105;
const SEND_CONTAINER_WIDTH = 430;
const ACTION_BUTTON_WIDTH = 200;
const ACTION_BUTTON_HEIGHT = 82;
const DETAILS_BINDING_Y = [190, 700];

export default class PopupCardResponse extends PopupBasic {
  private card: Card;
  private cardId!: CardId;
  private targetId?: string;
  private inviteRequest?: InviteRequestState;
  private requestMethod: CardsDeliveryMethod = 'request';
  private titleText: LangBitmapFontTextShadowView;
  private okayButton: ButtonScaleViewWithText;
  private sendContainer: View;

  constructor(opts: { superview: View; close: VoidFunction }) {
    super({
      superview: opts.superview,
      boxOpts: {
        image: 'assets/ui/cards/common/cardbook_frame.png',
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 80, center: 80, right: 80 },
          vertical: { top: 80, middle: 80, bottom: 80 },
        },
      },
      skipTitle: true,
      width: POPUP_WIDTH,
      height: POPUP_HEIGHT,
      close: () => {
        opts.close();
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.DETAILS,
      },
      zIndex: 10000,
    });

    this.buttonClose.hide();

    const titleBanner = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/cards/common/banner_title.png',
      width: downscale(576),
      height: downscale(132),
      x: CONTENT_CENTER_X,
      y: TITLE_BANNER_Y,
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    this.titleText = new LangBitmapFontTextShadowView({
      superview: titleBanner,
      width: titleBanner.style.width - 90,
      height: 58,
      x: titleBanner.style.width / 2,
      y: titleBanner.style.height / 2 - 10,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 36,
      font: 'Title',
      color: '#FFF5B5',
      textShadowColor: '#8D2E21',
      textShadowOffsetX: 0,
      textShadowOffsetY: 4,
      canHandleEvents: false,
      localeText: () => i18n('cards.responsePopup.title'),
    });

    for (const y of DETAILS_BINDING_Y) {
      new ImageScaleView({
        superview: this.box,
        image: 'assets/ui/cards/common/details_binding.png',
        width: 50,
        height: 79,
        x: 12,
        y,
        centerOnOrigin: true,
        scaleMethod: 'contain',
      });
    }

    this.card = new Card({
      superview: this.box,
      centerOnOrigin: true,
      x: CONTENT_CENTER_X,
      y: CARD_Y,
      scale: CARD_SCALE,
    });

    this.message.updateOpts({
      width: 500,
      height: 130,
      x: CONTENT_CENTER_X,
      y: MESSAGE_Y,
      font: 'Body',
      size: 30,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      color: '#9F5D58',
    });

    this.sendContainer = new View({
      superview: this.box,
      width: SEND_CONTAINER_WIDTH,
      height: ACTION_BUTTON_HEIGHT,
      x: CONTENT_CENTER_X,
      y: BUTTON_Y,
      centerOnOrigin: true,
    });

    new ButtonScaleViewWithText({
      superview: this.sendContainer,
      ...buttons.cancel,
      width: ACTION_BUTTON_WIDTH,
      height: ACTION_BUTTON_HEIGHT,
      x: 10,
      fontSize: 32,
      font: 'Body',
      labelOffsetX: -2,
      localeText: () => i18n('basic.cancel'),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.CANCEL,
      },
      onClick: async () => {
        sounds.sfx.play('cancel');
        opts.close();
      },
    });

    new ButtonScaleViewWithText({
      superview: this.sendContainer,
      ...buttons.secondary,
      width: ACTION_BUTTON_WIDTH,
      height: ACTION_BUTTON_HEIGHT,
      x: 220,
      fontSize: 32,
      font: 'Body',
      labelOffsetX: -2,
      localeText: () => i18n('cards.responsePopup.send'),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.SEND,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.handleSend(opts.close);
      },
    });

    this.okayButton = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      width: 320,
      height: ACTION_BUTTON_HEIGHT,
      x: CONTENT_CENTER_X,
      y: BUTTON_Y,
      centerOnOrigin: true,
      fontSize: 32,
      font: 'Body',
      localeText: () => i18n('basic.okay'),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.REQUEST,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        opts.close();
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup(AssetGroup.constructURLs(['assets/ui/cards/common']));
  }

  public init(opts: PopupCardResponseOpenArgs) {
    super.init({});

    if ('variant' in opts) {
      this.showAsReceiveLimitVariant();
      return;
    }

    const {
      cardState,
      targetId,
      inviteRequest,
      requestMethod,
      title,
      message,
      actionMode,
      buttonText,
    } = opts;

    this.titleText.localeText = () =>
      title ?? i18n('cards.responsePopup.title');
    this.card.setState(cardState);
    this.cardId = cardState.id;
    this.targetId = targetId;
    this.inviteRequest = inviteRequest;
    this.requestMethod = requestMethod ?? 'request';
    this.okayButton.localeText = () => buttonText ?? i18n('basic.okay');

    this.message.localeText = () => message;

    if (actionMode === 'send') {
      this.sendContainer.show();
      this.okayButton.hide();
    } else {
      this.sendContainer.hide();
      this.okayButton.show();
    }
  }

  /**
   * Show a terminal response message with an OK acknowledgment.
   *
   * @param message Localized message to display for the send outcome.
   */
  private showMessageWithOkayButton(message: string) {
    // Update the response copy for the terminal send state.
    this.message.localeText = () => message;

    // Prevent another send attempt and expose the acknowledgment action.
    this.sendContainer.hide();
    this.okayButton.show();
  }

  /**
   * Send the displayed card and render the resulting popup state.
   *
   * @param close Close callback used when the flow has no follow-up message.
   */
  private async handleSend(close: VoidFunction): Promise<void> {
    // Send actions require the requester identified by the share entry.
    const targetId = this.targetId;
    if (!targetId) {
      close();
      return;
    }

    // Delegate Replicant actions and updated card-state derivation to the sequence.
    const args: CardsSendRequestResponseArgs = this.inviteRequest
      ? {
          kind: 'redeem',
          requestedCardId: this.inviteRequest.requestedCardId,
          targetId,
          requestMethod: this.requestMethod,
        }
      : {
          kind: 'send',
          cardId: this.cardId,
          targetId,
          requestMethod: this.requestMethod,
        };
    const result = await cardsSendRequestResponse(args);

    switch (result.outcome) {
      // Requester receive limits remain visible for player acknowledgment.
      case 'rejected':
        if (result.reason === 'requester_receive_limit_reached') {
          this.showMessageWithOkayButton(
            i18n('cards.responsePopup.messageRequestRequesterReceivedLimit'),
          );
          return;
        }

        // Other authoritative rejections preserve the existing close behavior.
        close();
        return;

      // Invite redemption replaces the preview card and shows its success response.
      case 'redeemed':
        this.card.setState(result.cardState);
        this.showMessageWithOkayButton(
          i18n('cards.responsePopup.messageInviteSent', {
            level: cardsUnlockLevel,
          }),
        );
        return;

      // Normal duplicate sends finish by closing the response popup.
      case 'sent':
        close();
        return;

      default:
        assertNever(result);
    }
  }

  /** Configure the popup as a daily card receive-limit response. */
  private showAsReceiveLimitVariant() {
    this.titleText.localeText = () => i18n('cards.responsePopup.limitTitle');
    this.showMessageWithOkayButton(
      i18n('cards.responsePopup.messageReceivedLimit'),
    );
    this.card.hide();
    this.buttonClose.show();

    new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/cards/common/cards_hidden.png',
      width: 330,
      height: 330,
      x: CONTENT_CENTER_X,
      y: CARD_Y - 50,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      canHandleEvents: false,
    });

    new CardsDailyLimitTimer({
      superview: this.box,
      x: CONTENT_CENTER_X,
      y: 530,
    }).start('receive');
  }
}
